import React from 'react';
import { Mic2, Zap, Sliders, Globe2, Shield, Clock } from 'lucide-react';

const Features = () => {
  const features = [
    {
      icon: Mic2,
      title: 'Natural Voice Synthesis',
      description: 'Ultra-realistic voices powered by neural networks for human-like speech generation.',
      gradient: 'from-purple-600 to-indigo-600',
      iconGradient: 'from-purple-400 to-indigo-300',
      hoverGradient: 'hover:from-purple-700 hover:to-indigo-700',
    },
    {
      icon: Zap,
      title: 'Real-Time Processing',
      description: 'Generate high-quality audio in seconds with our optimized cloud infrastructure.',
      gradient: 'from-blue-500 to-cyan-400',
      iconGradient: 'from-blue-300 to-cyan-200',
      hoverGradient: 'hover:from-blue-600 hover:to-cyan-500',
    },
    {
      icon: Sliders,
      title: 'Voice Customization',
      description: 'Fine-tune pitch, speed, and emotion to create the perfect voice for your content.',
      gradient: 'from-pink-500 to-rose-500',
      iconGradient: 'from-pink-300 to-rose-300',
      hoverGradient: 'hover:from-pink-600 hover:to-rose-600',
    },
    {
      icon: Globe2,
      title: 'Multi-Language Support',
      description: 'Support for 50+ languages and regional accents for global content creation.',
      gradient: 'from-teal-500 to-emerald-500',
      iconGradient: 'from-teal-300 to-emerald-300',
      hoverGradient: 'hover:from-teal-600 hover:to-emerald-600',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-level encryption and data protection for your sensitive content.',
      gradient: 'from-amber-500 to-orange-600',
      iconGradient: 'from-amber-300 to-orange-400',
      hoverGradient: 'hover:from-amber-600 hover:to-orange-700',
    },
    {
      icon: Clock,
      title: 'Batch Processing',
      description: 'Convert multiple texts simultaneously for efficient content production.',
      gradient: 'from-violet-600 to-purple-600',
      iconGradient: 'from-violet-400 to-purple-400',
      hoverGradient: 'hover:from-violet-700 hover:to-purple-700',
    },
  ];

  return (
    <section id="features" className="py-20 bg-white dark:bg-dark-900 transition-colors duration-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 animate-gradient bg-gradient-to-r from-purple-400 via-pink-500 to-blue-500 bg-[length:200%_auto] bg-clip-text text-transparent">
            Best Text to Speech Features for AI Voice Generation
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Discover why QualityAIvoice is rated among the best text to speech software 2025 with advanced AI-powered voice generation features.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`p-6 rounded-xl overflow-hidden relative transition-all duration-300 transform hover:scale-[1.02] hover:shadow-xl ${feature.hoverGradient}`}
            >
              {/* Gradient background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${feature.gradient} opacity-90 dark:opacity-95`}></div>
              
              {/* Glass effect overlay */}
              <div className="absolute inset-0 bg-white/10 dark:bg-black/10 backdrop-blur-[1px]"></div>
              
              {/* Content with proper z-index */}
              <div className="relative z-10">
                {/* Icon with gradient background */}
                <div className={`w-14 h-14 rounded-lg flex items-center justify-center mb-5 bg-gradient-to-br ${feature.iconGradient} shadow-lg`}>
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                
                {/* Title and description */}
                <h3 className="text-xl font-bold text-white mb-3">
                  {feature.title}
                </h3>
                <p className="text-white/90 font-medium">{feature.description}</p>
              </div>
              
              {/* Decorative elements */}
              <div className="absolute -bottom-4 -right-4 w-24 h-24 rounded-full bg-white/10 blur-xl"></div>
              <div className="absolute top-1/2 -left-6 w-12 h-12 rounded-full bg-white/10 blur-lg"></div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Features;